import { useMemo } from 'react';

import {
  Chart as ChartJS,
  BarController,
  LineController,
  PieController,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  RadialLinearScale,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  ChartTypeRegistry,
  ChartType,
  ChartData,
  BubbleController,
  DoughnutController,
  PolarAreaController,
  RadarController,
  ScatterController,
} from 'chart.js';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Chart } from 'react-chartjs-2';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';

import { ReactComponent as TableIcon } from 'assets/svg/tableIcon.svg';
import { ReactComponent as VectorArrow } from 'assets/svg/vectorArrow.svg';
import { TableRow } from 'constants/reports-templates';
import { cn, getRandomColor } from 'utils/global';
ChartJS.register(
  BarController,
  LineController,
  PieController,
  BubbleController,
  DoughnutController,
  PolarAreaController,
  RadarController,
  ScatterController,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  RadialLinearScale,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);
type Props = {
  visualizationType?: 'Table' | string;
  columns?: any[];
  chartJsType: keyof ChartTypeRegistry;
  chartJsData: ChartData;
  tableData?: TableRow[];
  tableClassName?: string;
  graphClassName?: string;
};

const CreateReportTemplateSchedule: React.FC<Props> = ({
  visualizationType,
  columns = [],
  chartJsType,
  chartJsData,
  tableData = [],
  tableClassName,
  graphClassName,
}) => {
  const chartColors = useMemo(() => {
    const dataLength = chartJsData.labels?.length ?? 0;
    return Array.from({ length: dataLength }, () => getRandomColor(0.5));
  }, [chartJsData.labels?.length]);

  const coloredChartData = useMemo(
    () => ({
      ...chartJsData,
      datasets: chartJsData.datasets.map((dataset) => ({
        ...dataset,
        backgroundColor: chartColors,
        borderColor: 'rgba(0,0,0,0.1)',
        borderWidth: 1,
      })),
    }),
    [chartJsData, chartColors]
  );

  const shouldShowGrid = chartJsType === 'bar' || chartJsType === 'line' || chartJsType === 'scatter';

  const chartScales = shouldShowGrid
    ? {
        scales: {
          x: {
            title: {
              display: true,
              text: columns?.[0],
            },
            grid: {
              display: true,
            },
            ticks: {
              display: true,
            },
          },
          y: {
            title: {
              display: true,
              text: 'Count',
            },
            grid: {
              display: true,
            },
            ticks: {
              display: true,
            },
          },
        },
      }
    : {};

  const renderCellValue = (value: unknown): string => {
    if (typeof value === 'boolean') {
      return value.toString();
    }

    if (value === null || value === undefined || value === '') {
      return typeof value === 'boolean' ? 'false' : 'NULL';
    }

    return value instanceof Date ? value.toLocaleString() : value.toString();
  };

  if (!visualizationType) {
    return (
      <div className="p-6 border-r border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 overflow-auto h-full">
        <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
          <div className="mb-4 h-32 w-32 bg-neutral-100 rounded-full flex items-center justify-center">
            <TableIcon />
          </div>
          <p className="!text-md mb-3">No data to show yet</p>
          <p className="!text-sm">Add your first configuration to organize your data.</p>
          <VectorArrow className="ml-20 mt-4" />
        </div>
      </div>
    );
  }

  if (visualizationType === 'Table') {
    return (
      <div
        className={cn(
          'p-6 border-r border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 overflow-auto h-full',
          tableClassName
        )}
        style={{ width: 'calc(100vw - 41.25rem)' }}
      >
        <DataTable
          value={tableData}
          resizableColumns
          columnResizeMode="fit"
          showGridlines
          className="p-datatable-sm"
          scrollable
          scrollHeight="500px"
          tableStyle={{ borderCollapse: 'collapse', width: '100%' }}
        >
          {columns.map((col, index) => (
            <Column
              key={index}
              field={col.field}
              header={col.header}
              style={{ minWidth: '150px', border: '1px solid #ccc' }}
              body={(rowData: TableRow) => <span>{renderCellValue(rowData[col.field])}</span>}
            />
          ))}
        </DataTable>
      </div>
    );
  } else {
    return (
      <div
        className={cn(
          'flex justify-center border-r border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 overflow-auto h-full',
          graphClassName
        )}
      >
        <div className="p-1 w-[99%] h-[32rem]">
          <Chart
            type={chartJsType as ChartType}
            data={coloredChartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              ...chartScales,
            }}
          />
        </div>
      </div>
    );
  }
};

export default CreateReportTemplateSchedule;
